import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${window.location.origin}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helpers
const pad2 = (n) => String(n).padStart(2, '0');
const ymKey = (y, m) => `${y}-${pad2(m)}`;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Get Monday-start week containing a given date (returns array of 7 Date objects)
const weekContaining = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 0=Mon, 6=Sun
  const monday = new Date(d);
  monday.setDate(d.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return day;
  });
};

const sameYM = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function HabitTracker({ journeyDay, isTrialUser = false }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Active month being viewed (defaults to current calendar month)
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1); // 1-12
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'

  // For the week view, track which week we're on (default = week containing today)
  const [weekAnchor, setWeekAnchor] = useState(today);

  const [logDoc, setLogDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const yearMonth = ymKey(viewYear, viewMonth);
  const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth() + 1;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/habits/logs/${yearMonth}`, { headers: getAuthHeader() });
      setLogDoc(res.data);
    } catch (e) {
      console.error('Failed to load habit logs:', e);
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const habits = logDoc?.habits_snapshot || [];
  const days = logDoc?.days || {};
  const daysInMonth = logDoc?.days_in_month || 30;

  // Days to render for current view
  const weekDays = useMemo(() => weekContaining(weekAnchor), [weekAnchor]);

  // Get completion state for a specific date + habit
  const isDone = (date, habitId) => {
    if (!sameYM(date, new Date(viewYear, viewMonth - 1, 1))) return false;
    const dd = pad2(date.getDate());
    return Boolean(days[dd]?.[habitId]);
  };

  // Count completed today
  const todayDoneCount = useMemo(() => {
    if (!isCurrentMonth) return 0;
    const dd = pad2(today.getDate());
    const todayMap = days[dd] || {};
    return habits.reduce((acc, h) => acc + (todayMap[h.id] ? 1 : 0), 0);
  }, [days, habits, isCurrentMonth, today]);

  // Toggle a habit dot
  const toggleDot = async (date, habitId) => {
    // Don't allow future days
    if (date > today) return;
    // Optimistic update
    const dd = pad2(date.getDate());
    setLogDoc((prev) => {
      if (!prev) return prev;
      const next = { ...prev, days: { ...(prev.days || {}) } };
      next.days[dd] = { ...(next.days[dd] || {}) };
      next.days[dd][habitId] = !next.days[dd][habitId];
      return next;
    });

    try {
      setSaving(true);
      await axios.post(
        `${API}/habits/toggle`,
        { date: `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`, habit_id: habitId },
        { headers: getAuthHeader() }
      );
    } catch (e) {
      console.error('Toggle failed:', e);
      fetchLogs(); // revert by refetch
    } finally {
      setSaving(false);
    }
  };

  // Month navigator
  const goPrevMonth = () => {
    let y = viewYear, m = viewMonth - 1;
    if (m < 1) { m = 12; y -= 1; }
    setViewYear(y); setViewMonth(m);
    // Keep weekAnchor sensible — point at first day of new month
    setWeekAnchor(new Date(y, m - 1, 1));
  };
  const goNextMonth = () => {
    let y = viewYear, m = viewMonth + 1;
    if (m > 12) { m = 1; y += 1; }
    // Don't allow navigating past today's month
    const target = new Date(y, m - 1, 1);
    if (target > new Date(today.getFullYear(), today.getMonth() + 1, 1)) return;
    setViewYear(y); setViewMonth(m);
    setWeekAnchor(target);
  };

  const canGoNextMonth = !isCurrentMonth;

  // ===== Render =====
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45 }}
      className="glass-card rounded-2xl p-5 sm:p-6 mt-6 sm:mt-8"
      data-testid="habit-tracker"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-serif text-xl sm:text-2xl text-[#726158]">My Glow Up Habits</h2>
          <p className="text-[11px] sm:text-xs text-[#AA9283] mt-0.5">
            {MONTH_NAMES[viewMonth - 1]} {viewYear}
            {isCurrentMonth && journeyDay ? ` · Day ${today.getDate()} of ${daysInMonth}` : ` · ${daysInMonth} days`}
          </p>
        </div>

        {/* Week/Month toggle */}
        <div className="inline-flex rounded-full bg-[#FFFCF7] border border-[#EAD8C2] p-0.5 text-xs">
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1.5 rounded-full transition-colors ${viewMode === 'week' ? 'bg-[#B8956A] text-white' : 'text-[#AA9283]'}`}
            data-testid="habit-view-week"
          >
            Week
          </button>
          {isTrialUser ? (
            <span
              className="px-3 py-1.5 rounded-full text-[#AA9283]/70 inline-flex items-center gap-1 cursor-not-allowed select-none"
              title="Upgrade to unlock the full Month view"
              data-testid="habit-view-month-locked"
            >
              <Lock className="h-3 w-3" /> Month
            </span>
          ) : (
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 rounded-full transition-colors ${viewMode === 'month' ? 'bg-[#B8956A] text-white' : 'text-[#AA9283]'}`}
              data-testid="habit-view-month"
            >
              Month
            </button>
          )}
        </div>
      </div>

      {/* Trial soft-lock upgrade prompt for Month view */}
      {isTrialUser && (
        <Link
          to="/checkout?upgrade=true"
          className="block mb-3 px-3 py-2 rounded-lg bg-[#CAAB84]/10 border border-[#CAAB84]/30 text-center hover:bg-[#CAAB84]/20 transition-colors"
          data-testid="habit-trial-upgrade-prompt"
        >
          <span className="text-[12px] text-[#726158] inline-flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 text-[#B8956A]" />
            Upgrade to track your full 30-day habits
            <span className="text-[#B8956A] font-semibold">→</span>
          </span>
        </Link>
      )}

      {/* Month navigator (only show when not on current month OR in month view) */}
      {(viewMode === 'month' || !isCurrentMonth) && (
        <div className="flex items-center justify-between mb-4 text-xs text-[#726158]">
          <button
            onClick={goPrevMonth}
            className="p-1.5 rounded-full hover:bg-[#FFFCF7] transition-colors"
            data-testid="habit-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-medium">{MONTH_NAMES[viewMonth - 1]} {viewYear}</span>
          <button
            onClick={goNextMonth}
            disabled={!canGoNextMonth}
            className="p-1.5 rounded-full hover:bg-[#FFFCF7] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="habit-next-month"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="py-8 text-center text-sm text-[#AA9283]">Loading habits…</div>
      )}

      {/* ===== WEEK VIEW (default) ===== */}
      {!loading && viewMode === 'week' && (
        <WeekDotGrid
          weekDays={weekDays}
          habits={habits}
          isDone={isDone}
          toggleDot={toggleDot}
          today={today}
          viewYear={viewYear}
          viewMonth={viewMonth}
        />
      )}

      {/* ===== MONTH VIEW ===== */}
      {!loading && viewMode === 'month' && (
        <MonthDotGrid
          year={viewYear}
          month={viewMonth}
          daysInMonth={daysInMonth}
          habits={habits}
          isDone={isDone}
          toggleDot={toggleDot}
          today={today}
        />
      )}

      {/* Legend */}
      {!loading && habits.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#EAD8C2]/60" data-testid="habit-legend">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2">
            {habits.map((h) => (
              <div key={h.id} className="flex items-center gap-2 min-w-0">
                <span
                  className="inline-block rounded-full flex-shrink-0"
                  style={{ width: 10, height: 10, backgroundColor: h.color }}
                />
                <span className="text-[12px] text-[#726158] truncate">{h.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion counter */}
      {!loading && isCurrentMonth && (
        <div className="mt-4 text-center">
          <p className="text-[13px] font-medium" style={{ color: '#B8956A' }} data-testid="habit-completion-counter">
            {todayDoneCount} of {habits.length} habits completed today
          </p>
        </div>
      )}
    </motion.div>
  );
}

// ===== Weekly dot grid (7 columns × 5 dots) =====
function WeekDotGrid({ weekDays, habits, isDone, toggleDot, today, viewYear, viewMonth }) {
  const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="habit-week-grid">
      {weekDays.map((day, idx) => {
        const isFuture = day > today;
        const isToday = isSameDay(day, today);
        const inViewMonth = day.getFullYear() === viewYear && day.getMonth() + 1 === viewMonth;

        return (
          <div key={idx} className="flex flex-col items-center min-w-0">
            {/* Day label */}
            <div className="text-[10px] uppercase tracking-wide text-[#AA9283] mb-0.5">
              {DAY_LABELS[idx]}
            </div>
            {/* Day number */}
            <div
              className={`text-xs mb-2 ${isToday ? 'font-bold text-[#B8956A]' : 'text-[#726158]'} ${!inViewMonth ? 'opacity-40' : ''}`}
            >
              {day.getDate()}
            </div>

            {/* 5 dots */}
            <div className="flex flex-col items-center gap-1.5 sm:gap-2">
              {habits.map((h) => {
                const done = inViewMonth && isDone(day, h.id);
                const disabled = isFuture || !inViewMonth;
                return (
                  <button
                    key={h.id}
                    onClick={() => !disabled && toggleDot(day, h.id)}
                    disabled={disabled}
                    aria-label={`${h.name} on ${day.toDateString()}: ${done ? 'completed' : 'not completed'}`}
                    className={`block rounded-full transition-transform ${disabled ? 'cursor-not-allowed' : 'active:scale-90 hover:scale-110'}`}
                    style={{
                      width: 24,
                      height: 24,
                      backgroundColor: done ? h.color : 'transparent',
                      border: `2px solid ${disabled ? '#E5DDD3' : h.color}`,
                      opacity: disabled ? 0.4 : 1,
                    }}
                    data-testid={`habit-dot-${h.id}-${pad2(day.getDate())}`}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== Monthly dot grid (compact — show all days of month) =====
function MonthDotGrid({ year, month, daysInMonth, habits, isDone, toggleDot, today }) {
  const monthStart = new Date(year, month - 1, 1);
  // Determine starting day-of-week (Monday=0)
  const startDow = (monthStart.getDay() + 6) % 7;
  // We'll render a 7-column grid that pads with empty slots before day 1
  const totalSlots = startDow + daysInMonth;
  const rows = Math.ceil(totalSlots / 7);
  const slots = Array.from({ length: rows * 7 }, (_, i) => {
    const dayNum = i - startDow + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return null;
    return new Date(year, month - 1, dayNum);
  });
  const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div data-testid="habit-month-grid">
      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {DAY_LABELS.map((d, i) => (
          <div key={i} className="text-[9px] uppercase text-center text-[#AA9283]">{d}</div>
        ))}
      </div>
      {/* Day cells — each cell is the day number + 5 mini dots */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {slots.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const isFuture = day > today;
          const isToday = isSameDay(day, today);

          return (
            <div
              key={idx}
              className={`flex flex-col items-center p-1 rounded-md ${isToday ? 'bg-[#B8956A]/10' : ''}`}
            >
              <div
                className={`text-[10px] mb-1 ${isToday ? 'font-bold text-[#B8956A]' : 'text-[#726158]'}`}
              >
                {day.getDate()}
              </div>
              {/* 5 mini dots — render in a 5-dot vertical column */}
              <div className="flex flex-col gap-0.5 items-center">
                {habits.map((h) => {
                  const done = isDone(day, h.id);
                  const disabled = isFuture;
                  return (
                    <button
                      key={h.id}
                      onClick={() => !disabled && toggleDot(day, h.id)}
                      disabled={disabled}
                      aria-label={`${h.name} on ${day.toDateString()}`}
                      className={`block rounded-full transition-transform ${disabled ? 'cursor-not-allowed' : 'active:scale-90'}`}
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: done ? h.color : 'transparent',
                        border: `1.5px solid ${disabled ? '#E5DDD3' : h.color}`,
                        opacity: disabled ? 0.4 : 1,
                      }}
                      data-testid={`habit-mdot-${h.id}-${pad2(day.getDate())}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
